<template>
  <div class="ms-map-wrap">
    <div ref="mapEl" class="ms-map-canvas"></div>
    <!-- Draw an area to filter by. Top-left, opposite Google's own controls
         (fullscreen top-right, zoom bottom-right) and above the legend, which
         sits bottom-left. -->
    <div class="ms-draw-tools" role="group" aria-label="Draw an area to filter" data-tour="ms-draw-tools">
      <button v-for="tool in drawTools" :key="tool.mode" type="button" class="ms-draw-btn"
        :class="{ 'ms-draw-btn--active': drawMode === tool.mode }" :data-tour="'ms-draw-' + tool.mode"
        :title="tool.title" :aria-label="tool.title" :aria-pressed="String(drawMode === tool.mode)"
        @click="setDrawMode(tool.mode)">
        <font-awesome-icon :icon="tool.icon" />
      </button>
      <span class="ms-draw-sep"></span>
      <button type="button" class="ms-draw-btn ms-draw-btn--clear" data-tour="ms-draw-clear" :disabled="!hasArea"
        title="Clear drawn area" aria-label="Clear drawn area" @click="clearArea">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
      <!-- Replays the parent's tour. Opt-in, like `selectable`: a page that
           carries no tour would otherwise render a button that does nothing. -->
      <template v-if="helpButton">
        <span class="ms-draw-sep"></span>
        <button type="button" class="ms-draw-btn" data-tour="ms-draw-help" title="How does this work?"
          aria-label="How does this work?" @click="$emit('help')">
          <font-awesome-icon :icon="['fas', 'question']" />
        </button>
      </template>
    </div>
    <div v-if="drawMode" class="ms-draw-hint">{{ drawHint }}</div>
    <button v-if="!legendOpen" class="ms-legend-toggle ms-legend-toggle--show" type="button" title="Show legend"
      @click="legendOpen = true">
      <font-awesome-icon :icon="['fas', 'list-ul']" />
    </button>
    <div v-if="legendOpen" class="ms-map-legend">
      <button class="ms-legend-toggle ms-legend-toggle--hide" type="button" title="Hide legend"
        @click="legendOpen = false">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
      <div class="ms-legend-section">
        <div class="ms-legend-section-title">Status</div>
        <!-- Listed the way a project moves through the market: planning,
             pre-market, selling, then closed. -->
        <div class="ms-legend-item">
          <span class="ms-legend-dot" style="background:#244260"></span>In Planning
        </div>
        <div class="ms-legend-item">
          <span class="ms-legend-dot" style="background:#3C6289"></span>Selling / Leasing Soon
        </div>
        <div class="ms-legend-item">
          <span class="ms-legend-dot" style="background:#6C90B6"></span>Now Selling / Leasing
        </div>
        <div class="ms-legend-item">
          <span class="ms-legend-dot" style="background:#A1BCD8"></span>Sold / Leased Out
        </div>
      </div>
      <div class="ms-legend-divider"></div>
      <div class="ms-legend-section">
        <div class="ms-legend-section-title">Type</div>
        <div class="ms-legend-item">
          <svg width="13" height="13" viewBox="0 0 18 19">
            <circle cx="8.88" cy="9.66" r="8.88" fill="#6b7280" />
          </svg>
          Apartment / Mixed Use
        </div>
        <div class="ms-legend-item">
          <svg width="14" height="12" viewBox="0 0 25 22">
            <path d="M12.76 0.6L24.68 21.24H0.84L12.76 0.6Z" fill="#6b7280" />
          </svg>
          Townhome
        </div>
        <div class="ms-legend-item">
          <svg width="13" height="13" viewBox="0 0 19 19">
            <rect y="0.47" width="18.13" height="18.13" fill="#6b7280" />
          </svg>
          Commercial
        </div>
        <div class="ms-legend-item">
          <svg width="13" height="14" viewBox="0 0 19 20">
            <polygon points="9.5,1 18.06,7.22 14.79,17.28 4.21,17.28 0.94,7.22" fill="#6b7280" />
          </svg>
          Industrial
        </div>
        <div class="ms-legend-item">
          <svg width="14" height="12" viewBox="0 0 25 22">
            <path d="M12.5 0 L25 11 L12.5 22 L0 11 Z" fill="#6b7280" />
          </svg>
          Multiplex / Duplex
        </div>
      </div>
      <div class="ms-legend-divider"></div>
      <div class="ms-legend-count">{{ markerCount }} project{{ markerCount !== 1 ? 's' : '' }} on map</div>
      <button class="ms-legend-refresh" @click="initMap">
        <font-awesome-icon :icon="['fas', 'sync-alt']" /> Refresh
      </button>
    </div>
  </div>
</template>

<script>
import { distanceMetres, isDrawnArea } from "../../composables/mapAreaFilter";

const MAP_STYLES_BASE = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
];

const MAP_STYLES_LIGHT = [
  ...MAP_STYLES_BASE,
  { elementType: "geometry", stylers: [{ color: "#f1f3f5" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f1f3f5" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#ececee" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#e6e8eb" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#e3e6ea" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#dfe5dd" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#dcdfe3" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f4f5f7" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d1d5db" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#d8dde3" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#d1d5db" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
];

const MAP_STYLES_DARK = [
  ...MAP_STYLES_BASE,
  { elementType: "geometry", stylers: [{ color: "#0e1b2c" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0e1b2c" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ab4f8" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1c2f47" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#233a56" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#1d3652" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#255882" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#060e18" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d6b9f" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#0d1e30" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#1d3652" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#7fa8d0" }] },
];

const getMapStyles = () =>
  document.documentElement.classList.contains("dark") ? MAP_STYLES_DARK : MAP_STYLES_LIGHT;

const STATUS_FILL = {
  1: "#6C90B6", 2: "#6C90B6", 3: "#244260", 4: "#3C6289",
  5: "#3C6289", 6: "#A1BCD8", 7: "#A1BCD8",
};

const statusToIconId = (s) => {
  const lc = (s || "").toLowerCase();
  if (lc.includes("now selling")) return 1;
  if (lc.includes("now leasing")) return 2;
  if (lc.includes("planning")) return 3;
  if (lc.includes("leasing soon")) return 4;
  if (lc.includes("selling soon")) return 5;
  if (lc.includes("sold out")) return 6;
  return 7;
};

const projectTypeToIconType = (pt) => {
  const s = (pt || "").toLowerCase();
  if (s.includes("townhome")) return 2;
  if (s.includes("mixed")) return 3;
  if (s.includes("commercial")) return 4;
  // Duplex shares the diamond with Multiplex — both are small-scale multi-unit
  // forms, and "multiplex" does not contain "duplex", so the order is safe.
  if (s.includes("multiplex") || s.includes("duplex")) return 5;
  if (s.includes("industrial")) return 6;
  return 1;
};

const getIconSvg = (iconId, iconType) => {
  const f = STATUS_FILL[iconId] || "#A1BCD8";
  const shadow = `<defs><filter id="ds" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="1.5" stdDeviation="2" flood-color="rgba(0,0,0,0.45)"/></filter></defs>`;
  switch (Number(iconType)) {
    case 2:
      return `<svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">${shadow}<path d="M12.7622 0.601318L24.6807 21.2447H0.84379L12.7622 0.601318Z" fill="${f}" filter="url(#ds)"/></svg>`;
    case 3:
      return `<svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">${shadow}<circle cx="8.88167" cy="9.6578" r="7.88167" fill="${f}" filter="url(#ds)"/></svg>`;
    case 4:
      return `<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">${shadow}<rect x="0.75" y="1.22" width="17.13" height="17.13" fill="${f}" filter="url(#ds)"/></svg>`;
    case 5:
      return `<svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">${shadow}<path d="M12.5 1L24 11L12.5 21L1 11Z" fill="${f}" filter="url(#ds)"/></svg>`;
    case 6:
      return `<svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">${shadow}<polygon points="9.5,1.5 17.56,7.22 14.79,16.78 4.21,16.78 1.44,7.22" fill="${f}" filter="url(#ds)"/></svg>`;
    default:
      return `<svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">${shadow}<circle cx="8.88167" cy="9.6578" r="7.88167" fill="${f}" filter="url(#ds)"/></svg>`;
  }
};

const extractLatLng = (row) => {
  const g = row.geo;
  if (!g) return null;
  const lat = parseFloat(g.lat);
  const lng = parseFloat(g.lng);
  if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
  return null;
};

// Fill and stroke for the drawn area. Deliberately not a --rpd-* token: these
// are painted onto Google's canvas by the Maps API, which takes colour strings
// and cannot read CSS custom properties. --rpd-coral / --rpd-navy-600 are the
// values they mirror, so the shape reads as part of the app in either theme.
const AREA_SHAPE_STYLE = {
  fillColor: "#F26A6F",
  fillOpacity: 0.12,
  strokeColor: "#D94B52",
  strokeWeight: 2,
  clickable: false,
};

const DRAW_TOOLS = [
  { mode: "polygon", icon: ["fas", "draw-polygon"], title: "Draw a shape" },
  { mode: "rectangle", icon: ["fas", "vector-square"], title: "Draw a rectangle" },
  { mode: "circle", icon: ["fas", "circle-notch"], title: "Draw a circle" },
];

const DRAW_HINTS = {
  polygon: "Click to drop points, double-click to finish. Esc to cancel.",
  rectangle: "Drag across the map to draw a rectangle. Esc to cancel.",
  circle: "Drag out from the centre to draw a circle. Esc to cancel.",
};

// Map options a drawing session overrides, and the values it puts back. Written
// out rather than read off the map first: Map.get() does not reliably report
// options that were never set explicitly, and these are the values initMap()
// creates the map with.
const MAP_OPTIONS_AFTER_DRAW = {
  draggable: true,
  disableDoubleClickZoom: false,
  draggableCursor: null,
};

// Below these a drag reads as a stray click rather than a shape, and committing
// it would filter every project away for no reason the user can see.
const MIN_CIRCLE_RADIUS_M = 25;
const MIN_RECT_SPAN_DEG = 0.0001;

const latLngToPlain = (latLng) => ({ lat: latLng.lat(), lng: latLng.lng() });

// The box two dragged corners describe, in either drag direction.
const dragBounds = (a, b) => ({
  north: Math.max(a.lat, b.lat),
  south: Math.min(a.lat, b.lat),
  east: Math.max(a.lng, b.lng),
  west: Math.min(a.lng, b.lng),
});

// A kept overlay the user has just dragged → the plain, serialisable shape the
// parent holds. See composables/mapAreaFilter.js for why the plain form travels.
const overlayToArea = (type, overlay) => {
  if (type === "circle") {
    const c = overlay.getCenter();
    return {
      kind: "circle",
      center: { lat: c.lat(), lng: c.lng() },
      radius: overlay.getRadius(),
    };
  }
  if (type === "rectangle") {
    const b = overlay.getBounds();
    return {
      kind: "rectangle",
      bounds: {
        north: b.getNorthEast().lat(),
        east: b.getNorthEast().lng(),
        south: b.getSouthWest().lat(),
        west: b.getSouthWest().lng(),
      },
    };
  }
  return {
    kind: "polygon",
    path: overlay
      .getPath()
      .getArray()
      .map((p) => ({ lat: p.lat(), lng: p.lng() })),
  };
};

export default {
  name: "ProjectMap",

  props: {
    rows: { type: Array, default: () => [] },
    clusterMode: {
      type: String,
      default: "both",
      validator: (v) => ["both", "type", "status", "none"].includes(v),
    },
    center: {
      type: Object,
      default: () => ({ lat: 50.484446697484394, lng: -120.69252345856054 }),
    },
    defaultZoom: { type: Number, default: 7 },
    maxFitZoom: { type: Number, default: 14 },
    selectedUid: { type: [String, Number], default: null },
    // Opt-in: renders the selection checkbox in the marker popup. Only pages
    // that keep a multi-project selection (and listen for `toggle-select`)
    // should turn it on — elsewhere the control would be a dead button.
    selectable: { type: Boolean, default: false },
    // The parent's current multi-selection, read when a popup is built so the
    // checkbox opens in the right state. Intentionally not watched: the popup
    // updates its own checkbox in place, so a selection change must never
    // rebuild the markers and close the popup that caused it.
    selectedUids: { type: Array, default: () => [] },
    pageTitle: { type: String, default: "" },
    // The drawn filter area, owned by the parent so it can filter rows with it.
    // Watched only for identity changes the parent makes (clearing it, or
    // restoring a saved view) — the overlay reports its own edits upward, and
    // re-rendering it from the value it just emitted would fight the user's
    // drag. See `_areaEcho`.
    area: { type: Object, default: null },
    // Renders the "?" beside the drawing tools, which emits `help`. Only a page
    // that answers it — by starting a tour of its own — should turn this on.
    helpButton: { type: Boolean, default: false },
  },

  data() {
    // The legend opens with the map every time: it is what explains the pin
    // colours, so a first look should never start with it collapsed. Hiding it
    // lasts for as long as the map stays mounted, and is not remembered.
    return { markerCount: 0, legendOpen: true, drawMode: null };
  },

  computed: {
    drawTools() {
      return DRAW_TOOLS;
    },
    drawHint() {
      return DRAW_HINTS[this.drawMode] || "";
    },
    hasArea() {
      return isDrawnArea(this.area);
    },
  },

  mounted() {
    this.$nextTick(() => this.initMap());
  },

  beforeDestroy() {
    this.cleanup();
  },

  watch: {
    rows() { this.initMap(); },
    clusterMode() { this.initMap(); },
    selectedUid(uid) { this.focusRow(uid); },
    area(val) {
      // Skip the echo of our own emit — the overlay already shows that shape.
      if (this._areaEcho === val) {
        this._areaEcho = null;
        return;
      }
      this._areaEcho = null;
      this.renderArea(val);
    },
  },

  methods: {
    // ── Drawing ─────────────────────────────────────────────────────────
    //
    // Hand-rolled on plain Polygon / Rectangle / Circle overlays and the map's
    // own mouse events. google.maps.drawing.DrawingManager would be the obvious
    // tool, but Google removed it in Maps JS 3.65 — constructing one throws.
    // Nothing below is deprecated.
    //
    // A session is: arm a tool (listeners on, map options overridden, a preview
    // overlay that follows the cursor) → commit into a kept, editable overlay,
    // or cancel. `_preview` is the in-progress shape; `_areaOverlay` the kept
    // one. They are never the same object, so an abandoned drag leaves the
    // previous area untouched.

    beginDrawSession(mode) {
      const map = this._mapInstance;
      this._drawListeners = [];
      map.setOptions({
        draggableCursor: "crosshair",
        // Rectangle and circle are drawn by dragging, which would otherwise pan
        // the map out from under the shape. Polygon is click-to-place, so
        // panning between points stays useful.
        draggable: mode === "polygon",
        // Otherwise the double-click that closes a polygon also zooms in.
        disableDoubleClickZoom: mode === "polygon",
      });

      this._onDrawKeyDown = (e) => {
        if (e.key === "Escape") this.setDrawMode(null);
        else if (e.key === "Enter" && mode === "polygon") this.commitPolygon();
      };
      window.addEventListener("keydown", this._onDrawKeyDown);
    },

    endDrawSession() {
      (this._drawListeners || []).forEach((l) => google.maps.event.removeListener(l));
      this._drawListeners = [];
      if (this._onDrawKeyDown) {
        window.removeEventListener("keydown", this._onDrawKeyDown);
        this._onDrawKeyDown = null;
      }
      if (this._onWindowMouseUp) {
        window.removeEventListener("mouseup", this._onWindowMouseUp);
        this._onWindowMouseUp = null;
      }
      this.dropPreview();
      this._drawPoints = null;
      if (this._mapInstance) this._mapInstance.setOptions(MAP_OPTIONS_AFTER_DRAW);
    },

    dropPreview() {
      if (this._preview) {
        this._preview.setMap(null);
        this._preview = null;
      }
      this._dragStart = null;
    },

    // The in-progress shape is click-through so the map keeps reporting the
    // mouse under it — without this a drag stalls the moment the pointer moves
    // over what has been drawn so far.
    previewOptions() {
      return { ...AREA_SHAPE_STYLE, clickable: false, map: this._mapInstance };
    },

    armDrawing(mode) {
      const map = this._mapInstance;
      if (!map) return false;
      this.beginDrawSession(mode);
      const on = (ev, fn) =>
        this._drawListeners.push(google.maps.event.addListener(map, ev, fn));

      if (mode === "polygon") {
        this._drawPoints = [];
        on("click", (e) => this.addPolygonPoint(e.latLng));
        on("mousemove", (e) => this.trackPolygonCursor(e.latLng));
        on("dblclick", () => this.commitPolygon());
      } else {
        on("mousedown", (e) => this.startDrag(mode, e.latLng));
        on("mousemove", (e) => this.updateDrag(mode, e.latLng));
        on("mouseup", () => this.commitDrag(mode));
        // The pointer can be released outside the canvas — only the window sees
        // that, and without it the drag would never end.
        this._onWindowMouseUp = () => this.commitDrag(mode);
        window.addEventListener("mouseup", this._onWindowMouseUp);
      }
      return true;
    },

    paintPolygonPreview(path) {
      if (!this._preview) {
        this._preview = new google.maps.Polygon(this.previewOptions());
      }
      this._preview.setPath(path);
    },

    addPolygonPoint(latLng) {
      if (!this._drawPoints) return;
      this._drawPoints.push(latLngToPlain(latLng));
      this.paintPolygonPreview(this._drawPoints);
    },

    // Rubber-band from the last placed point to the cursor.
    trackPolygonCursor(latLng) {
      if (!this._drawPoints || !this._drawPoints.length) return;
      this.paintPolygonPreview([...this._drawPoints, latLngToPlain(latLng)]);
    },

    commitPolygon() {
      // A double-click fires two `click`s first, so the closing point arrives
      // twice — and a slow hand can place the same point twice besides.
      const path = (this._drawPoints || []).filter((p, i, all) => {
        const prev = all[i - 1];
        return !prev || prev.lat !== p.lat || prev.lng !== p.lng;
      });
      // Two points are a line, not an area: keep the tool armed rather than
      // committing something that would filter everything away.
      if (path.length < 3) return;
      this.finishShape({ kind: "polygon", path });
    },

    startDrag(mode, latLng) {
      this.dropPreview();
      const start = latLngToPlain(latLng);
      this._dragStart = start;
      this._preview =
        mode === "rectangle"
          ? new google.maps.Rectangle({
            ...this.previewOptions(),
            bounds: dragBounds(start, start),
          })
          : new google.maps.Circle({
            ...this.previewOptions(),
            center: start,
            radius: 1,
          });
    },

    updateDrag(mode, latLng) {
      if (!this._dragStart || !this._preview) return;
      const now = latLngToPlain(latLng);
      if (mode === "rectangle") {
        this._preview.setBounds(dragBounds(this._dragStart, now));
      } else {
        this._preview.setRadius(Math.max(1, distanceMetres(this._dragStart, now)));
      }
    },

    // Called from both the map's mouseup and the window's, and again from
    // endDrawSession's teardown — idempotent, because the first call clears
    // `_dragStart`.
    commitDrag(mode) {
      const start = this._dragStart;
      const preview = this._preview;
      if (!start || !preview) return;
      if (mode === "rectangle") {
        const b = preview.getBounds();
        const ne = b && b.getNorthEast();
        const sw = b && b.getSouthWest();
        if (
          !ne ||
          ne.lat() - sw.lat() < MIN_RECT_SPAN_DEG ||
          Math.abs(ne.lng() - sw.lng()) < MIN_RECT_SPAN_DEG
        ) {
          // A click, not a drag. Leave the tool armed for a real one.
          return this.dropPreview();
        }
        this.finishShape({
          kind: "rectangle",
          bounds: {
            north: ne.lat(),
            east: ne.lng(),
            south: sw.lat(),
            west: sw.lng(),
          },
        });
      } else {
        const radius = preview.getRadius();
        if (!(radius >= MIN_CIRCLE_RADIUS_M)) return this.dropPreview();
        this.finishShape({ kind: "circle", center: start, radius });
      }
    },

    // Preview → the kept, editable overlay, and up to the parent. Drawing is a
    // one-shot action: staying armed would have the next click start another
    // shape instead of panning the map.
    finishShape(area) {
      this.setDrawMode(null);
      // renderArea() clears the previous area and wires the edit listeners.
      this.renderArea(area);
      this._areaEcho = area;
      this.$emit("area-change", area);
    },

    // Re-emit while the user drags a vertex, edge or radius handle, so the row
    // filter tracks the shape instead of waiting for it to be redrawn.
    watchAreaOverlay() {
      const overlay = this._areaOverlay;
      const type = this._areaOverlayType;
      if (!overlay) return;
      const emit = () => this.emitArea();
      const listeners = [];
      if (type === "polygon") {
        const path = overlay.getPath();
        ["set_at", "insert_at", "remove_at"].forEach((ev) =>
          listeners.push(google.maps.event.addListener(path, ev, emit))
        );
      } else if (type === "rectangle") {
        listeners.push(google.maps.event.addListener(overlay, "bounds_changed", emit));
      } else {
        listeners.push(google.maps.event.addListener(overlay, "radius_changed", emit));
        listeners.push(google.maps.event.addListener(overlay, "center_changed", emit));
      }
      this._areaListeners = listeners;
    },

    emitArea() {
      if (!this._areaOverlay) return;
      const area = overlayToArea(this._areaOverlayType, this._areaOverlay);
      // Remembered so the `area` watcher can tell the parent echoing this value
      // back from a shape the parent set itself.
      this._areaEcho = area;
      this.$emit("area-change", area);
    },

    removeAreaOverlay() {
      (this._areaListeners || []).forEach((l) => google.maps.event.removeListener(l));
      this._areaListeners = [];
      if (this._areaOverlay) {
        this._areaOverlay.setMap(null);
        this._areaOverlay = null;
      }
      this._areaOverlayType = null;
    },

    // Plain shape → overlay. Used when the parent sets an area this map did not
    // draw: a saved view being restored, or a map instance rebuilt from scratch.
    renderArea(area) {
      if (!this._mapInstance) return;
      this.removeAreaOverlay();
      if (!isDrawnArea(area)) return;
      const map = this._mapInstance;
      let overlay;
      if (area.kind === "circle") {
        overlay = new google.maps.Circle({
          ...AREA_SHAPE_STYLE,
          editable: true,
          map,
          center: area.center,
          radius: area.radius,
        });
      } else if (area.kind === "rectangle") {
        overlay = new google.maps.Rectangle({
          ...AREA_SHAPE_STYLE,
          editable: true,
          map,
          bounds: area.bounds,
        });
      } else {
        overlay = new google.maps.Polygon({
          ...AREA_SHAPE_STYLE,
          editable: true,
          map,
          paths: area.path,
        });
      }
      this._areaOverlay = overlay;
      this._areaOverlayType = area.kind;
      this.watchAreaOverlay();
    },

    setDrawMode(mode) {
      // Same button again = put the tool down.
      const next = this.drawMode === mode ? null : mode;
      if (this.drawMode) this.endDrawSession();
      this.drawMode = null;
      if (!next) return;
      if (this.armDrawing(next)) this.drawMode = next;
    },

    clearArea() {
      this.setDrawMode(null);
      this.removeAreaOverlay();
      this._areaEcho = null;
      this.$emit("area-change", null);
    },

    isUidSelected(uid) {
      return uid != null && this.selectedUids.indexOf(uid) !== -1;
    },

    // Reflects `on` onto the popup's checkbox. The popup lives outside Vue's
    // render tree, so its state is written directly.
    paintSelectButton(btn, on) {
      btn.classList.toggle("ms-map-popup-select--on", on);
      btn.setAttribute("aria-pressed", String(on));
      const label = on ? "Remove from selection" : "Add to selection";
      btn.setAttribute("title", label);
      btn.setAttribute("aria-label", label);
    },

    focusRow(uid) {
      if (!uid || !this._mapInstance || !this._mapMarkers) return;
      const marker = this._mapMarkers.find((m) => m._row && m._row.uid === uid);
      if (!marker) return;
      this._mapInstance.panTo(marker.getPosition());
      if (this._mapInstance.getZoom() < 13) this._mapInstance.setZoom(13);
      google.maps.event.trigger(marker, "spider_click");
    },

    cleanup() {
      (this._clusterers || []).forEach((c) => c.clearMarkers());
      (this._mapMarkers || []).forEach((m) => m.setMap(null));
      this._clusterers = [];
      this._mapMarkers = [];
      if (this._mapThemeObserver) {
        this._mapThemeObserver.disconnect();
        this._mapThemeObserver = null;
      }
      if (this._infoWindow) {
        this._infoWindow.close();
        this._infoWindow = null;
      }
      this.endDrawSession();
      this.removeAreaOverlay();
      this.drawMode = null;
      this._mapInstance = null;
      this._didInitialFit = false;
    },

    initMap() {
      const el = this.$refs.mapEl;
      if (!el || typeof google === "undefined") return;

      (this._clusterers || []).forEach((c) => c.clearMarkers());
      (this._mapMarkers || []).forEach((m) => m.setMap(null));
      this._clusterers = [];
      this._mapMarkers = [];

      let map = this._mapInstance;
      if (!map) {
        map = new google.maps.Map(el, {
          center: new google.maps.LatLng(this.center.lat, this.center.lng),
          zoom: this.defaultZoom,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
          styles: getMapStyles(),
        });
        this._mapInstance = map;

        google.maps.event.addListener(map, "click", (e) => {
          console.log("[ProjectMap] click", {
            zoom: map.getZoom(),
            position: { lat: e.latLng.lat(), lng: e.latLng.lng() },
          });
        });

        // Report the camera after every pan/zoom settles. A parent that hides
        // the map with v-if (destroying this component) can hold the last
        // value and feed it back through `center` / `defaultZoom`, so the view
        // survives the round trip instead of snapping to the defaults.
        google.maps.event.addListener(map, "idle", () => {
          const c = map.getCenter();
          if (!c) return;
          this.$emit("camera-change", {
            center: { lat: c.lat(), lng: c.lng() },
            zoom: map.getZoom(),
          });
        });

        const themeObserver = new MutationObserver(() => {
          this._mapInstance && this._mapInstance.setOptions({ styles: getMapStyles() });
        });
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        this._mapThemeObserver = themeObserver;
        // A fresh map instance (first open, or a return from Table view) starts
        // with a bare canvas, so an area the parent is still holding has to be
        // drawn back onto it.
        this.renderArea(this.area);
      } else {
        map.setOptions({ styles: getMapStyles() });
      }

      const bounds = new google.maps.LatLngBounds();

      // One popup for the map's lifetime, closed on every rebuild. Toggling a
      // selection from the popup can change `rows` (e.g. "Filter to selected"),
      // and a fresh InfoWindow each time would leave the old one hanging over
      // a marker that no longer exists.
      if (this._infoWindow) this._infoWindow.close();
      else this._infoWindow = new google.maps.InfoWindow();
      const infoWindow = this._infoWindow;

      const oms = typeof OverlappingMarkerSpiderfier !== "undefined"
        ? new OverlappingMarkerSpiderfier(map, {
          markersWontMove: true,
          markersWontHide: true,
          nearbyDistance: 20,
          keepSpiderfied: true,
        })
        : null;

      this.rows.forEach((row) => {
        const coords = extractLatLng(row);
        if (!coords) return;
        const { lat, lng } = coords;

        const iconId = statusToIconId(row.status);
        const iconType = projectTypeToIconType(row.project_type);
        const svg = getIconSvg(iconId, iconType);

        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(lat, lng),
          map,
          icon: {
            url: "data:image/svg+xml;charset=UTF-8;base64," + btoa(svg),
            scaledSize: new google.maps.Size(26, 26),
            anchor: new google.maps.Point(13, 13),
          },
          title: row.name || "",
        });

        google.maps.event.addListener(marker, "spider_click", () => {
          this.$emit("select", row.uid);
          // Rental statuses (Now Leasing, Now Leasing 2, Leasing Soon, Leased
          // Out) show $PSF with 2 decimals; strata statuses use whole dollars.
          const psfStatus = (row.status || "").toLowerCase();
          const psfDecimals =
            psfStatus.includes("leasing") || psfStatus.includes("leased")
              ? 2
              : 0;
          const psf = row.total_net_psf
            ? `$${Number(row.total_net_psf).toLocaleString("en-CA", {
              minimumFractionDigits: psfDecimals,
              maximumFractionDigits: psfDecimals,
            })} $PSF`
            : "";
          const units = row.total_units ? `${row.total_units} Units` : "";

          const photos = row.field_construction_photos;
          const hasPhotos = photos && typeof photos === "object" && photos !== "-" && Object.keys(photos).length > 0;
          const imgUrl = hasPhotos
            ? Object.values(photos)[0].thumb
            : (row.pic && row.pic.filename
              ? `https://www.realpropertydata.ca/backend/sites/default/files/${row.pic.filename}`
              : "/images/assets/placeholder.png");

          const nameHtml = row.ref_link
            ? `<a class="ms-map-popup-name ms-map-popup-name--link" href="${row.ref_link}" target="_blank" rel="noopener">${row.name || ""}</a>`
            : `<span class="ms-map-popup-name">${row.name || ""}</span>`;
          const statusBadgeClass = (() => {
            const s = (row.status || "").toLowerCase();
            let cls = "ms-badge";
            if (s.includes("now selling") || s.includes("now leasing")) cls += " ms-badge--active";
            else if (s.includes("selling soon") || s.includes("leasing soon")) cls += " ms-badge--soon";
            else if (s.includes("sold out") || s.includes("leased out")) cls += " ms-badge--out";
            else if (s.includes("planning")) cls += " ms-badge--planning";
            return cls;
          })();
          const detailIconSvg = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14L21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/></svg>`;
          const checkIconSvg = `<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;
          // Mirrors the project-list checkbox: same toggle, same selection.
          const selectHtml = this.selectable
            ? `<button type="button" class="ms-map-popup-select${this.isUidSelected(row.uid) ? " ms-map-popup-select--on" : ""}" data-popup-select="1" aria-pressed="${this.isUidSelected(row.uid)}" title="${this.isUidSelected(row.uid) ? "Remove from selection" : "Add to selection"}" aria-label="${this.isUidSelected(row.uid) ? "Remove from selection" : "Add to selection"}">${checkIconSvg}</button>`
            : "";
          const mapMarkerSvg = `<svg class="ms-project-location-icon" viewBox="0 0 384 512" width="9" height="9" fill="currentColor" aria-hidden="true"><path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"/></svg>`;

          infoWindow.setContent(`<div class="ms-map-popup">
            <div class="ms-map-popup-img" style="background-image:url('${imgUrl}')"></div>
            <div class="ms-map-popup-body">
              <div class="ms-map-popup-name-row">
                ${nameHtml} 
                ${row.status ? `<span class="${statusBadgeClass}">${row.status}</span>` : ""}
                ${selectHtml}
                <button type="button" class="ms-map-popup-detail" data-popup-detail="1" title="View project details" aria-label="View project details">${detailIconSvg}</button>
              </div>
              ${row.developer ? `<div class="ms-map-popup-dev">${row.developer} ${row.city ? `${mapMarkerSvg} ${row.city}` : ""}</div>` : ""}
              <div class="ms-map-popup-meta">
                ${row.project_type ? `<span class="ms-map-popup-chip">${row.project_type}</span>` : ""}
                ${row.type ? `<span class="ms-map-popup-chip">${row.type}</span>` : ""}
              </div>
              ${(() => {
              const s = (row.status || "").toLowerCase();
              const hideStats = s.includes("selling soon") || s.includes("leasing soon") || s.includes("planning");
              return !hideStats && (psf || units) ? `<div class="ms-map-popup-stats">${[psf, units].filter(Boolean).join(" &nbsp;·&nbsp; ")}</div>` : "";
            })()}
            </div>
          </div>`);

          google.maps.event.addListenerOnce(infoWindow, "domready", () => {
            const btn = document.querySelector('button[data-popup-detail="1"]');
            if (btn) {
              btn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.$emit("open-detail", row);
              };
            }

            const selectBtn = document.querySelector('button[data-popup-select="1"]');
            if (selectBtn) {
              selectBtn.onclick = (e) => {
                e.preventDefault();
                // Keep the popup open — the click must not reach the map.
                e.stopPropagation();
                // Derived before the emit: the `selectedUids` prop only
                // reaches this component on the parent's next render, so
                // re-reading it here would still see the old selection.
                const next = !this.isUidSelected(row.uid);
                this.$emit("toggle-select", row.uid);
                // The popup is plain HTML outside Vue's render tree, so its
                // new state is painted by hand.
                this.paintSelectButton(selectBtn, next);
              };
            }
          });

          infoWindow.open(map, marker);
        });

        marker._iconType = iconType;
        marker._iconId = iconId;
        marker._row = row;
        if (oms) oms.addMarker(marker);
        bounds.extend(marker.getPosition());
        this._mapMarkers.push(marker);
      });

      this.markerCount = this._mapMarkers.length;

      // Initial load uses the configured `center` / `defaultZoom` (set when the
      // map instance was created) rather than auto-fitting to marker bounds, so
      // the map always opens at the default view. Later rebuilds (e.g. filter
      // changes) reuse the same instance and preserve the user's current pan/zoom.

      if (typeof MarkerClusterer !== "undefined" && this._mapMarkers.length > 1 && this.clusterMode !== "none") {
        const mode = this.clusterMode;
        const groups = {};
        this._mapMarkers.forEach((m) => {
          const key = mode === "type" ? String(m._iconType)
            : mode === "status" ? String(m._iconId)
              : `${m._iconId}-${m._iconType}`;
          if (!groups[key]) groups[key] = { markers: [], iconType: m._iconType, iconId: m._iconId };
          groups[key].markers.push(m);
        });

        Object.values(groups).forEach(({ markers, iconType, iconId }) => {
          const svg = getIconSvg(iconId, iconType);
          const b64 = "data:image/svg+xml;charset=UTF-8;base64," + btoa(svg);
          const clusterer = new MarkerClusterer(map, markers, {
            minimumClusterSize: 2,
            styles: [
              { url: b64, height: 50, width: 50, anchor: [16, 16], textColor: "#000000", textSize: 11 },
            ],
          });
          clusterer.setMaxZoom(15);
          google.maps.event.addListener(clusterer, "clusterclick", (cluster) => {
            map.fitBounds(cluster.getBounds());
            if (map.getZoom() > 16) map.setZoom(16);
          });
          this._clusterers.push(clusterer);
        });
      }
    },
  },
};
</script>

<style scoped>
.ms-map-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
}

.ms-map-canvas {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.ms-map-legend {
  position: absolute;
  bottom: 28px;
  left: 12px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
  z-index: 10;
  min-width: 184px;
  pointer-events: auto;
  padding-right: 26px;
}

.ms-draw-tools {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.16);
}

.ms-draw-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #374151;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}

.ms-draw-btn:hover:not(:disabled) {
  background: #f3f4f6;
  color: #12283f;
}

.ms-draw-btn--active {
  background: var(--rpd-navy-600);
  color: #ffffff;
}

.ms-draw-btn--active:hover:not(:disabled) {
  background: var(--rpd-navy-700);
  color: #ffffff;
}

.ms-draw-btn--clear:hover:not(:disabled) {
  background: rgba(242, 106, 111, 0.14);
  color: var(--rpd-coral-dark);
}

.ms-draw-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.ms-draw-sep {
  width: 1px;
  height: 16px;
  background: #e5e7eb;
  margin: 0 2px;
}

.ms-draw-hint {
  position: absolute;
  top: 46px;
  left: 10px;
  z-index: 10;
  max-width: 230px;
  padding: 6px 9px;
  border-radius: 6px;
  background: rgba(14, 27, 44, 0.9);
  color: #ffffff;
  font-size: 10.5px;
  line-height: 1.35;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.ms-legend-toggle {
  position: absolute;
  z-index: 11;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: rgba(255, 255, 255, 0.96);
  color: #374151;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  pointer-events: auto;
  font-family: inherit;
}

.ms-legend-toggle:hover {
  background: #f3f4f6;
  color: #12283f;
}

.ms-legend-toggle--hide {
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.ms-legend-toggle--hide:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #12283f;
}

.ms-legend-toggle--show {
  bottom: 28px;
  left: 12px;
}

.ms-legend-section {
  margin-bottom: 2px;
}

.ms-legend-section-title {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  margin-bottom: 5px;
}

.ms-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 4px;
  color: #374151;
  font-size: 11px;
}

.ms-legend-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ms-legend-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 8px 0;
}

.ms-legend-count {
  font-size: 10px;
  color: #6b7280;
}

.ms-legend-refresh {
  margin-top: 7px;
  width: 100%;
  height: 26px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  background: #f9fafb;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-family: inherit;
  transition: background 0.12s;
}

.ms-legend-refresh:hover {
  background: #f0f1f3;
}

/* Dark mode */
html.dark .ms-map-legend {
  background: rgba(14, 27, 44, 0.96);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

html.dark .ms-draw-tools {
  background: rgba(14, 27, 44, 0.96);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

html.dark .ms-draw-btn {
  color: rgba(255, 255, 255, 0.7);
}

html.dark .ms-draw-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

html.dark .ms-draw-btn--active,
html.dark .ms-draw-btn--active:hover:not(:disabled) {
  background: var(--rpd-navy-400);
  color: #0e1b2c;
}

html.dark .ms-draw-btn--clear:hover:not(:disabled) {
  background: rgba(242, 106, 111, 0.2);
  color: var(--rpd-coral);
}

html.dark .ms-draw-sep {
  background: rgba(255, 255, 255, 0.12);
}

html.dark .ms-draw-hint {
  background: rgba(0, 0, 0, 0.82);
}

html.dark .ms-legend-section-title {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .ms-legend-item {
  color: rgba(255, 255, 255, 0.7);
}

html.dark .ms-legend-divider {
  background: rgba(255, 255, 255, 0.08);
}

html.dark .ms-legend-count {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .ms-legend-refresh {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.6);
}

html.dark .ms-legend-refresh:hover {
  background: rgba(255, 255, 255, 0.12);
}

html.dark .ms-legend-toggle {
  background: rgba(14, 27, 44, 0.96);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
}

html.dark .ms-legend-toggle:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

html.dark .ms-legend-toggle--hide {
  background: rgba(14, 27, 44, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
}

html.dark .ms-legend-toggle--hide:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

/* ── Responsive: tablet ─────────────────────────────────── */
@media (max-width: 991.98px) {
  .ms-map-legend {
    max-height: calc(100% - 56px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* ── Responsive: mobile ─────────────────────────────────── */
@media (max-width: 575.98px) {
  .ms-map-legend {
    bottom: 12px;
    left: 8px;
    right: 8px;
    min-width: 0;
    max-width: calc(100% - 16px);
    max-height: 45vh;
    padding: 10px 12px 10px 12px;
    padding-right: 30px;
    border-radius: 8px;
    overflow-y: auto;
  }

  .ms-legend-toggle--show {
    bottom: 12px;
    left: 8px;
  }

  .ms-draw-tools {
    top: 8px;
    left: 8px;
  }

  .ms-draw-btn {
    width: 32px;
    height: 32px;
  }

  .ms-draw-hint {
    top: 48px;
    left: 8px;
    right: 8px;
    max-width: none;
  }

  .ms-legend-section-title {
    margin-bottom: 4px;
  }

  .ms-legend-item {
    font-size: 10.5px;
    margin-bottom: 3px;
  }

  .ms-legend-divider {
    margin: 6px 0;
  }

  /* Scrollbar styling inside legend */
  .ms-map-legend::-webkit-scrollbar {
    width: 4px;
  }

  .ms-map-legend::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }

  .ms-map-legend::-webkit-scrollbar-track {
    background: transparent;
  }
}
</style>

<style>
/* Google InfoWindow content lives outside Vue's scoped DOM, so these must be global */
.ms-map-popup {
  min-width: 200px;
  max-width: 240px;
  padding: 0;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
}

.ms-map-popup-img {
  width: 100%;
  height: 120px;
  background-size: cover;
  background-position: center;
  background-color: #e5e7eb;
  flex-shrink: 0;
}

.ms-map-popup-body {
  padding: 8px 10px 6px;
}

.ms-map-popup-name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 2px;
}

.ms-map-popup-name {
  font-size: 13px;
  font-weight: 600;
  color: #0e1b2c;
  display: block;
  flex: 1;
  min-width: 0;
}

.ms-map-popup-detail {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}

.ms-map-popup-detail:hover {
  background: #e5e7eb;
  color: #12283f;
  border-color: #9ca3af;
}

/* Selection checkbox — same amber accent as the project-list checkbox so a
   project reads as selected the same way in both places. */
.ms-map-popup-select {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1.5px solid #c9ccd2;
  border-radius: 5px;
  background: #ffffff;
  color: transparent;
  cursor: pointer;
  transition: background 120ms, color 120ms, border-color 120ms;
}

.ms-map-popup-select:hover {
  border-color: #d97706;
  color: #e5c9a3;
}

.ms-map-popup-select--on,
.ms-map-popup-select--on:hover {
  background: #d97706;
  border-color: #d97706;
  color: #ffffff;
}

.ms-map-popup-name--link {
  text-decoration: none;
  color: #0e1b2c;
}

.ms-map-popup-name--link:hover {
  text-decoration: underline;
  color: #1a56db;
}

.ms-map-popup-dev {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 5px;
}

.ms-map-popup-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}

.ms-map-popup-status {
  font-size: 10px;
  background: #e5e7eb;
  color: #374151;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
}

.ms-map-popup-chip {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 999px;
  background: #eef3f8;
  color: #12283f;
  white-space: nowrap;
}

html.dark .ms-map-popup-chip {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.78);
}

.ms-map-popup .ms-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(84, 84, 84, 0.1);
  color: #545454;
  white-space: nowrap;
}

.ms-map-popup .ms-badge::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8a8a8a;
  flex-shrink: 0;
}

.ms-map-popup .ms-badge--active {
  background: rgba(31, 157, 85, 0.1);
  color: #1f7a45;
}

.ms-map-popup .ms-badge--active::before {
  background: #1f9d55;
}

.ms-map-popup .ms-badge--soon {
  background: rgba(255, 184, 28, 0.14);
  color: #8a6a00;
}

.ms-map-popup .ms-badge--soon::before {
  background: #ffb81c;
}

.ms-map-popup .ms-badge--out {
  background: rgba(84, 84, 84, 0.1);
  color: #545454;
}

.ms-map-popup .ms-badge--out::before {
  background: #8a8a8a;
}

.ms-map-popup .ms-badge--planning {
  background: rgba(52, 51, 118, 0.1);
  color: #343376;
}

.ms-map-popup .ms-badge--planning::before {
  background: #343376;
}

.ms-map-popup-city {
  font-size: 10px;
  color: #9ca3af;
  align-self: center;
}

.ms-map-popup-stats {
  font-size: 11px;
  font-weight: 600;
  color: #059669;
  margin-top: 1px;
}

html.dark .ms-map-popup {
  background: #0e1b2c;
}

html.dark .ms-map-popup-img {
  background-color: #1c2f47;
}

html.dark .ms-map-popup-body {
  background: #0e1b2c;
}

html.dark .ms-map-popup-name {
  color: #e8f0fe;
}

html.dark .ms-map-popup-detail {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.75);
}

html.dark .ms-map-popup-detail:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.3);
  color: #ffffff;
}

html.dark .ms-map-popup-select {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.2);
}

html.dark .ms-map-popup-select:hover {
  border-color: #fbbf24;
  color: rgba(251, 191, 36, 0.55);
}

html.dark .ms-map-popup-select--on,
html.dark .ms-map-popup-select--on:hover {
  background: #d97706;
  border-color: #d97706;
  color: #ffffff;
}

html.dark .ms-map-popup-name--link {
  color: #8ab4f8;
}

html.dark .ms-map-popup-name--link:hover {
  color: #aecbfa;
}

html.dark .ms-map-popup-dev {
  color: rgba(255, 255, 255, 0.45);
}

html.dark .ms-map-popup-status {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

html.dark .ms-map-popup-city {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .ms-map-popup-stats {
  color: #34d399;
}
</style>
